// frontend/src/pages/CookiePolicy.js

import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Cookie as CookieIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Campaign as CampaignIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

const CookiePolicy = () => {
  const effectiveDate = 'January 1, 2025';
  const lastUpdated = 'January 1, 2025';

  const cookieCategories = [
    {
      name: 'Essential Cookies',
      icon: <SecurityIcon />,
      description: 'Required for the website to function properly',
      canDisable: false,
      cookies: [
        {
          name: 'auth-token',
          purpose: 'Maintains your login session',
          duration: 'Session',
          type: 'First-party',
        },
        {
          name: 'firebase-auth',
          purpose: 'Firebase authentication',
          duration: '1 year',
          type: 'Third-party (Google)',
        },
        {
          name: 'csrf-token',
          purpose: 'Security - prevents cross-site request forgery',
          duration: 'Session',
          type: 'First-party',
        },
      ],
    },
    {
      name: 'Functional Cookies',
      icon: <SettingsIcon />,
      description: 'Remember your preferences and settings',
      canDisable: true,
      cookies: [
        {
          name: 'user-preferences',
          purpose: 'Stores your UI preferences (theme, layout)',
          duration: '1 year',
          type: 'First-party',
        },
        {
          name: 'ai-settings',
          purpose: 'Remembers your AI feature preferences',
          duration: '1 year',
          type: 'First-party',
        },
        {
          name: 'recording-session',
          purpose: 'Maintains voice recording session state',
          duration: '24 hours',
          type: 'First-party',
        },
      ],
    },
    {
      name: 'Analytics Cookies',
      icon: <AnalyticsIcon />,
      description: 'Help us understand how you use our service',
      canDisable: true,
      cookies: [
        {
          name: '_ga',
          purpose: 'Google Analytics - tracks unique visitors',
          duration: '2 years',
          type: 'Third-party (Google)',
        },
        {
          name: '_gid',
          purpose: 'Google Analytics - distinguishes users',
          duration: '24 hours',
          type: 'Third-party (Google)',
        },
        {
          name: 'usage-stats',
          purpose: 'Tracks feature usage for product improvement',
          duration: '30 days',
          type: 'First-party',
        },
      ],
    },
    {
      name: 'Marketing Cookies',
      icon: <CampaignIcon />,
      description: 'Used to show relevant ads to free-tier users',
      canDisable: true,
      cookies: [
        {
          name: 'google-ads',
          purpose: 'Google Ads tracking and remarketing',
          duration: '90 days',
          type: 'Third-party (Google)',
        },
        {
          name: 'conversion-tracking',
          purpose: 'Tracks free to pro conversions',
          duration: '30 days',
          type: 'First-party',
        },
      ],
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 6 } }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CookieIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
            <Typography variant="h3" component="h1" fontWeight={700}>
              Cookie Policy
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Effective Date: {effectiveDate} | Last Updated: {lastUpdated}
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Introduction */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            About This Cookie Policy
          </Typography>
          <Typography paragraph>
            ideaHive ("we", "us", or "our") uses cookies and similar tracking technologies to provide, 
            improve, and protect our AI-powered note-taking service. This Cookie Policy explains what 
            cookies are, how we use them, and your choices regarding their use.
          </Typography>
          <Typography paragraph>
            By using ideaHive, you consent to the use of cookies in accordance with this policy. 
            If you do not agree to our use of cookies, you can adjust your browser settings or 
            manage your preferences using our cookie consent tool.
          </Typography>
        </Box>

        {/* What Are Cookies */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            What Are Cookies?
          </Typography>
          <Typography paragraph>
            Cookies are small text files that are placed on your device when you visit a website. 
            They help websites remember information about your visit, such as your preferences and 
            login status, making your next visit easier and more productive.
          </Typography>
        </Box>

        {/* Types of Cookies We Use */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600} sx={{ mb: 3 }}>
            Types of Cookies We Use
          </Typography>
          
          {cookieCategories.map((category, index) => (
            <Accordion key={index} defaultExpanded={index === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {category.icon}
                  <Box>
                    <Typography variant="h6">{category.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {category.description}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cookie Name</TableCell>
                        <TableCell>Purpose</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {category.cookies.map((cookie, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {cookie.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{cookie.purpose}</TableCell>
                          <TableCell>{cookie.duration}</TableCell>
                          <TableCell>{cookie.type}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {category.canDisable && (
                  <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                    * These cookies can be disabled in your account settings
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Third-Party Cookies */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Third-Party Cookies
          </Typography>
          <Typography paragraph>
            Some cookies are placed by third-party services that appear on our pages. We do not 
            control these cookies and cannot access them. Third-party cookies on our site include:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Google (Firebase, Analytics, Ads)"
                secondary="Authentication, analytics, and advertising services"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="OpenAI"
                secondary="May set cookies when processing AI requests"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Stripe (Future)"
                secondary="Payment processing for Pro subscriptions"
              />
            </ListItem>
          </List>
        </Box>

        {/* How to Manage Cookies */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            How to Manage Cookies
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Through ideaHive Settings
          </Typography>
          <Typography paragraph>
            You can manage your cookie preferences directly in your account settings. 
            Go to Settings → Privacy & Cookies to control which non-essential cookies you allow.
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Through Your Browser
          </Typography>
          <Typography paragraph>
            Most web browsers allow you to control cookies through their settings:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary={<Link href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener">Chrome</Link>}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary={<Link href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener">Firefox</Link>}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary={<Link href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener">Safari</Link>}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary={<Link href="https://support.microsoft.com/en-us/windows/manage-cookies-in-microsoft-edge-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener">Edge</Link>}
              />
            </ListItem>
          </List>
          
          <Typography variant="body2" sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            <strong>Note:</strong> Disabling essential cookies may prevent you from using certain features 
            of ideaHive, including logging in to your account.
          </Typography>
        </Box>

        {/* Chrome Extension */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Chrome Extension Cookies
          </Typography>
          <Typography paragraph>
            Our Chrome extension may access cookies from ideaHive to maintain your authentication 
            and sync your preferences. The extension does not set additional cookies on third-party 
            websites you visit.
          </Typography>
        </Box>

        {/* Updates to This Policy */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Updates to This Policy
          </Typography>
          <Typography paragraph>
            We may update this Cookie Policy from time to time to reflect changes in our practices 
            or for legal reasons. We will notify you of any material changes by posting the new 
            policy on this page and updating the "Last Updated" date.
          </Typography>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Contact Us
          </Typography>
          <Typography paragraph>
            If you have questions about our use of cookies or this Cookie Policy, please contact us at:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Email"
                secondary="privacy@blackmount.ai"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Website"
                secondary={<Link href="https://app.blackmount.ai">app.blackmount.ai</Link>}
              />
            </ListItem>
          </List>
        </Box>

        {/* Cookie Settings Button */}
        <Box sx={{ mt: 6, p: 3, bgcolor: 'grey.100', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Manage Your Cookie Preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can change your cookie settings at any time
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<CookieIcon />}
            onClick={() => window.location.href = '/settings#cookies'}
          >
            Cookie Settings
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default CookiePolicy;